// @ts-check
// bookmarklets.js — "Bookmarklets" settings section (web only).
//
// Extracted from settings-panel.js (Stage B of the monolith split). Renders
// the two draggable bookmarklet links (Learn Page / Extract), derives the
// palette the payloads bake in from the current theme, and re-derives the
// hrefs when the appearance setting changes.
//
// The payload source strings live in ../bookmarklets/*.bookmarklet.js (moved
// out in Stage A); this module only builds the UI + palette. renderBookmarklets
// (ctx) uses ctx.row, ctx.rawRow, ctx.openExpansions.

import { getCurrentTheme } from '../../ui/theme-engine.js';
import { buildExtractSrc } from '../bookmarklets/extract.bookmarklet.js';
import { buildLearnSrc } from '../bookmarklets/learn-page.bookmarklet.js';
import type { SettingsSectionCtx } from '../settings-section-api.js';
import { Settings } from '../settings-store.js';

// Held across renders so re-entering renderBookmarklets can drop the
// previous Settings.subscribe before registering a new one. Without this,
// each render leaked a subscriber that rebuilt URI-encoded blobs on every
// theme tweak.
/** @type {any} */
let _bookmarkletsUnsubscribe: any = null;

/**
 * @param {string} bg - background hex.
 * @param {string} accent - accent hex.
 * @returns {any} the derived palette baked into the bookmarklet payloads.
 */
function _deriveBookmarkletPalette(bg: any, accent: any) {
  /**
   * @param {string} hex
   * @returns {number[]} [h, s, l]
   */
  function hexToHsl(hex: any) {
    const r = parseInt(hex.slice(1, 3), 16) / 255,
      g = parseInt(hex.slice(3, 5), 16) / 255,
      b = parseInt(hex.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b),
      min = Math.min(r, g, b);
    let h = 0,
      s = 0;
    const l = (max + min) / 2;
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
      else if (max === g) h = ((b - r) / d + 2) / 6;
      else h = ((r - g) / d + 4) / 6;
    }
    return [h * 360, s * 100, l * 100];
  }
  /**
   * @param {number} h
   * @param {number} s
   * @param {number} l
   * @returns {string} a #rrggbb hex colour.
   */
  function hslToHex(h: any, s: any, l: any) {
    h = ((h % 360) + 360) % 360;
    s = Math.min(100, Math.max(0, s));
    l = Math.min(100, Math.max(0, l));
    h /= 360;
    s /= 100;
    l /= 100;
    let r: number, g: number, b: number;
    if (s === 0) {
      r = g = b = l;
    } else {
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s,
        p = 2 * l - q;
      const h2r = (
        /** @type {any} */ p: any,
        /** @type {any} */ q: any,
        /** @type {any} */ t: any
      ) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        return t < 1 / 6
          ? p + (q - p) * 6 * t
          : t < 0.5
            ? q
            : t < 2 / 3
              ? p + (q - p) * (2 / 3 - t) * 6
              : p;
      };
      r = h2r(p, q, h + 1 / 3);
      g = h2r(p, q, h);
      b = h2r(p, q, h - 1 / 3);
    }
    return (
      '#' +
      [r, g, b]
        .map((x) =>
          Math.round(x * 255)
            .toString(16)
            .padStart(2, '0')
        )
        .join('')
    );
  }
  const cl = (/** @type {any} */ v: any, /** @type {any} */ a: any, /** @type {any} */ b: any) =>
    Math.min(b, Math.max(a, v));
  const [bgH, bgS, bgL] = hexToHsl(bg);
  const [acH, acS, acL] = hexToHsl(accent);
  const dk = bgL < 50;
  return {
    bg,
    surface: hslToHex(bgH, cl(bgS * 0.5 + acS * 0.15, 0, 35), cl(dk ? bgL + 9 : bgL - 7, 8, 85)),
    inputBg: hslToHex(bgH, cl(bgS * 0.6, 0, 20), cl(dk ? bgL - 4 : bgL + 4, 4, 96)),
    border: hslToHex(acH, cl(acS * 0.55, 0, 50), cl(dk ? bgL + 22 : bgL - 18, 10, 50)),
    subtle: hslToHex(acH, cl(acS * 0.4, 0, 35), cl(dk ? bgL + 13 : bgL - 12, 8, 45)),
    text: hslToHex(
      acH,
      cl(acS * 0.25, 0, 25),
      cl(dk ? cl(93 - bgL * 0.1, 82, 94) : cl(bgL * 0.12, 8, 18), 8, 94)
    ),
    muted: hslToHex(acH, cl(acS * 0.35, 0, 35), cl(dk ? bgL + 44 : bgL - 28, 48, 75)),
    dim: hslToHex(acH, cl(acS * 0.3, 0, 30), cl(dk ? bgL + 22 : bgL - 16, 32, 65)),
    accent,
    accentBg: hslToHex(acH, cl(acS * 0.8, 20, 70), cl(dk ? bgL + 16 : bgL - 14, 10, 35)),
    accentDark: hslToHex(acH, cl(acS * 0.9, 15, 65), cl(dk ? acL - 12 : acL - 18, 15, 42)),
    accentLight: hslToHex(acH, cl(acS * 0.65, 20, 65), cl(dk ? acL + 18 : acL - 8, 60, 88)),
    code: hslToHex((acH + 150) % 360, 30, dk ? 68 : 35),
    // Status colours — mirror the app's theme-derive.ts (success hue 145,
    // warning 35, error 0; lightness keyed on dark/light) so the injected
    // panels' status chrome tracks the theme instead of hardcoding greens.
    success: hslToHex(145, 50, dk ? 55 : 38),
    successBg: hslToHex(145, dk ? 30 : 45, dk ? 16 : 88),
    warning: hslToHex(35, 65, dk ? 60 : 45),
    warningBg: hslToHex(35, dk ? 30 : 50, dk ? 15 : 88),
    error: hslToHex(0, 65, dk ? 60 : 45),
    errorBg: hslToHex(0, dk ? 30 : 50, dk ? 15 : 85),
  };
}

/**
 * @param {any} ctx - the section render context.
 * @returns {any[]} the section's rows.
 */
export function renderBookmarklets(ctx: SettingsSectionCtx) {
  if (!window.Platform?.isWeb) return []; // runtime — Platform has no ES export

  const rows = [];
  const ID = 'group:bookmarklets';

  rows.push(
    ctx.row({
      id: ID,
      label: 'Bookmarklets',
      value: '2',
      expandable: true,
      searchExpand: true,
      cls: 'settings-row-group',
      searchText:
        'bookmarklets extract learn page web clip scrape css selector excel row paste layout blank cell',
    })
  );
  if (!ctx.openExpansions.has(ID)) return rows;

  // Build the draggable controls ONCE (passed as a factory). The reconciler
  // retains this node across re-renders — including a theme repaint's full
  // render — so the drag handlers survive untouched, and a single
  // Settings.subscribe('appearance') re-derives the payload hrefs in place on
  // the retained links. A collapse→expand remount cleans up + re-registers the
  // subscription, so it never leaks.
  const buildControls = (): HTMLElement => {
    const wrap = document.createElement('div');
    wrap.className = 'bookmarklet-controls';

    const btnRow = document.createElement('div');
    btnRow.className = 'bookmarklet-row';

    // Reuse the `.btn` primitive (surface fill, soft corner, hover tint, press
    // scale) so the links track the app's button language; `.bookmarklet-btn`
    // only layers the drag affordance on top.
    const learnLink = document.createElement('a');
    learnLink.className = 'btn bookmarklet-btn';
    learnLink.textContent = 'Learn Page';
    learnLink.draggable = true;
    learnLink.addEventListener('click', (e) => e.preventDefault());

    const extractLink = document.createElement('a');
    extractLink.className = 'btn bookmarklet-btn';
    extractLink.textContent = 'Extract';
    extractLink.draggable = true;
    extractLink.addEventListener('click', (e) => e.preventDefault());

    const updateHrefs = () => {
      const theme =
        typeof getCurrentTheme === 'function'
          ? getCurrentTheme()
          : { background: '#fee4a8', accent: '#000000' };
      const p = _deriveBookmarkletPalette(theme.background, theme.accent);
      learnLink.href = 'javascript:' + encodeURIComponent(buildLearnSrc(p));
      extractLink.href = 'javascript:' + encodeURIComponent(buildExtractSrc(p));
    };
    updateHrefs();

    addFaviconSwap(learnLink, '🧠');
    addFaviconSwap(extractLink, '📋');

    if (Settings) {
      if (_bookmarkletsUnsubscribe) _bookmarkletsUnsubscribe();
      _bookmarkletsUnsubscribe = Settings.subscribe('appearance', () => updateHrefs());
    }

    btnRow.appendChild(learnLink);
    btnRow.appendChild(extractLink);
    wrap.appendChild(btnRow);

    const hint = document.createElement('p');
    hint.className = 'bookmarklet-hint';
    hint.textContent = 'Drag to your bookmarks bar. Show it with Ctrl+Shift+B (⌘+Shift+B on Mac).';
    wrap.appendChild(hint);

    return wrap;
  };

  rows.push(
    ctx.rawRow(buildControls, {
      key: 'bookmarklet-controls',
      indent: true,
      searchText: 'bookmarklets learn page extract drag bookmark',
    })
  );

  return rows;
}

/**
 * Swap the page favicon to an emoji while a bookmarklet link is pressed/dragged,
 * then restore it shortly after. Module-level so the controls factory reaches it.
 * @param {any} link @param {string} emoji @returns {void}
 */
function addFaviconSwap(link: any, emoji: any) {
  const svgUri =
    'data:image/svg+xml,' +
    encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">` +
        `<text y=".9em" font-size="90">${emoji}</text></svg>`
    );
  let dragging = false;
  /** @type {any} */
  let restoreTimer: any = null;
  function setEmoji() {
    if (restoreTimer) {
      clearTimeout(restoreTimer);
      restoreTimer = null;
    }
    let el = document.getElementById('_pex-fav') as any;
    if (!el) {
      el = document.createElement('link');
      el.rel = 'icon';
      el.id = '_pex-fav';
      el.type = 'image/svg+xml';
      document.head.appendChild(el);
    }
    el.href = svgUri;
  }
  function restoreOrig(/** @type {any} */ delay: any) {
    restoreTimer = setTimeout(() => {
      const el = document.getElementById('_pex-fav');
      if (el) el.remove();
      restoreTimer = null;
    }, delay);
  }
  link.addEventListener('mousedown', setEmoji);
  link.addEventListener('dragstart', (/** @type {any} */ e: any) => {
    dragging = true;
    setEmoji();
    const c = document.createElement('canvas');
    c.width = c.height = 64;
    const ctx = c.getContext('2d') as any;
    ctx.font = '52px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(emoji, 32, 36);
    e.dataTransfer.setDragImage(c, 32, 32);
  });
  link.addEventListener('dragend', () => {
    dragging = false;
    restoreOrig(200);
  });
  link.addEventListener('mouseup', () => {
    if (!dragging) restoreOrig(50);
  });
}
